import type { MaterialInfo, PartDefinition } from '@rogue/rackbuilder'
import { AdjustOnlyVisual } from '../../../constants'
import type { SquatStandOptions } from '../../../RackTypes'
import { SquatStandBuilder } from '../squatStandBuilder'
import { getRackModel } from '../../../modelPaths'

/**
 * HR-2 Half Rack variant
 * Extends SML with additional back support structure, different height logic,
 * and pipe safeties instead of spotter arms
 */
export class HR2HalfRack extends SquatStandBuilder {
  protected safeties: number | undefined
  protected largeHalfRack: boolean
  protected textureBlackMaterial

  constructor(config: SquatStandOptions) {
    super(config)
    
    this.safeties = config.safeties
    this.largeHalfRack = !!config.largeHalfRack
    this.textureBlackMaterial = this.createTextureBlackMaterial()
  }

  // HR-2 always allows pullup bars
  protected shouldAddPullupBar(): boolean {
    return true
  }

  protected createTextureBlackMaterial(): MaterialInfo {
    return {
      main     : { material: 'powdercoat' },
      hardware : { material: this.hardware },
    }
  }

  protected getUprightMeshLine(height: '70' | '90' | '108'): string {
    return height === '90' ? 'SML_COMMON_PARTS' : 'SML_UPRIGHT_VARIANTS'
  }

  protected getFrontUprightHeight(): '90' | '108' {
    return this.largeHalfRack ? '108' : '90'
  }

  protected getBackUprightHeight(): '70' | '90' {
    return this.largeHalfRack ? '90' : '70'
  }

  protected createUpright(left: boolean): PartDefinition {
    const frontHeight = this.getFrontUprightHeight()
    const meshLine = this.getUprightMeshLine(frontHeight)
    
    const jcup = this.createJCup(left)

    const upright: PartDefinition = {
      mesh  : getRackModel(meshLine, `upright${frontHeight}`), 
      slots : { 
        'jcup-high'       : jcup,
        'low-crossmember' : this.createRoguePlate(left),
      },
      anchor      : 'bottom',
      materials   : this.textureBlackMaterial,
      adjustments : left ? [] : [
        { type: 'rotate', axis: 'Z', amount: 180 }, 
        { type: 'flip', axis: 'Y' },
      ],
      adjustmentType: left ? undefined : AdjustOnlyVisual,
    }

    if (this.safeties) {
      upright.slots.spotterarms = this.createPipeSafety()
    }

    if (left && this.shouldAddPullupBar()) {
      const pullupBar = this.createPullupBar()
      if (pullupBar) {
        upright.slots.top = pullupBar
      }
    }

    if (left && this.line === 'SML') {
      upright.slots.sticker_left = this.createUprightSticker()
    }

    return upright
  }

  // Override base creation - add HR-2 back plate and stick
  protected createBase(left: boolean): PartDefinition {
    const base = super.createBase(left)
    base.materials = this.textureBlackMaterial
    
    if (left) {
      delete base.slots['back-sticker']
    }

    // Add back support structure
    base.slots['hr-2-back-plate'] = this.createBackPlate(left)
    base.slots['hr-2-back-stick'] = this.createBackStick(left) 

    return base
  }

  // Override back stabilizer - HR-2 doesn't have hardware
  protected createBackStabilizer(): PartDefinition {
    return {
      mesh      : getRackModel('SML_COMMON_PARTS', 'back'),
      slots     : {}, // No hardware slot
      materials : this.textureBlackMaterial,
    }
  }

  // HR-2 Specific Parts
  protected createPipeSafety(): PartDefinition {
    return {
      mesh   : getRackModel('HR2_PARTS', 'pipe-safety-1'),
      slots  : {},
      anchor : 'front',
    }
  }

  protected createRoguePlate(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('HR2_PARTS', 'hr-2 rogue plate'),
      slots       : {},
      anchor      : 'front',
      materials   : this.textureBlackMaterial,
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      offset      : left ? { z: -0.2 } : { z: -19.3 },
    }
  }

  protected createBackPlate(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('HR2_PARTS', 'hr-2 back plate'),
      slots       : {},
      anchor      : 'back',
      materials   : this.textureBlackMaterial,
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
      offset      : left ? { x: 0.35, z: 0.5 } : { x: -0.35, z: 0.5 },
    }
  }

  protected createBackStick(left: boolean): PartDefinition {
    const backHeight = this.getBackUprightHeight() 
    const meshLine = this.getUprightMeshLine(backHeight)
    
    return {
      mesh        : getRackModel(meshLine, `upright${backHeight}`),
      slots       : {},
      anchor      : 'bottom',
      materials   : this.textureBlackMaterial,
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
    }
  }
}